import { v4 } from 'uuid'

import { createThunk } from '@app/store/thunk'

const setReduxSliceType = '@REDUX_SLICE/setReduxSlice'
export type SetReduxSliceAction<T = unknown> = { type: typeof setReduxSliceType; payload: { container: string; key: string; data: T } }
const setReduxSlice = (payload: { container: string; key: string; data: any }): SetReduxSliceAction => ({ type: setReduxSliceType, payload })

export const isSetReduxSliceAction = (action: { type: string }): action is SetReduxSliceAction => action.type === setReduxSliceType

export class ReduxSliceContainer {
  readonly reducerKey: string

  private keys: string[] = []
  private serializers: { key: string; serialize: (value: any) => any; deserialize: (value: any) => any }[] = []

  constructor(reducerKey?: string) {
    this.reducerKey = reducerKey ?? `redux_slice_container_${v4()}`
  }

  readonly reducer = (state: Record<string, any> = {}, action: { type: string }) => {
    if (isSetReduxSliceAction(action) && action.payload.container === this.reducerKey) {
      // Writing the same reference back is not a change: without this the new container
      // object notifies every subscriber of every slice in it.
      if (state[action.payload.key] === action.payload.data && action.payload.key in state) return state
      return { ...state, [action.payload.key]: action.payload.data }
    }
    return state
  }

  readonly createSlice = <T>(key: string) => {
    if (this.keys.includes(key)) {
      // Two modules claiming one key is a bug worth stopping for. The same module evaluated twice
      // is not: hmr reloads the chunk that defines a slice, and the handle below addresses the
      // store rather than holding state, so the second one works on what the first left behind.
      if (BUILD_MODE !== 'dev') throw new Error(`Redux slice with key ${key} already exists`)

      console.warn(`Redux slice with key ${key} already exists — re-registering it, which is what an hmr reload of its module looks like`)
      this.serializers = this.serializers.filter(serializer => serializer.key !== key)
    } else {
      this.keys.push(key)
    }

    const obj = {
      key,
      selector: (state: unknown) => (state as any)[this.reducerKey]?.[key] as T | undefined,
      get: () => createThunk((_dispatch, getState) => (getState() as any)[this.reducerKey][key] as T | undefined),
      set: (value: T) =>
        createThunk(dispatch => {
          dispatch(setReduxSlice({ container: this.reducerKey, key, data: value }))
          return dispatch(obj.get()) as T
        }),
      update: (cb: (val: T | undefined) => T | undefined) =>
        createThunk(dispatch => dispatch(setReduxSlice({ container: this.reducerKey, key, data: cb(dispatch(obj.get())) }))),
      remove: () => setReduxSlice({ container: this.reducerKey, key, data: undefined }),
      serialize: (serialize: (value: T | undefined) => any, deserialize: (value: any) => T | undefined) => {
        this.serializers.push({ key, serialize: serialize as (value: any) => any, deserialize: deserialize as (value: any) => any })
        return obj
      },
      cleanup: () => {
        this.keys = this.keys.filter(k => k !== key)
        this.serializers = this.serializers.filter(s => s.key !== key)
      },
      [Symbol.dispose]: () => obj.cleanup(),
    }

    return obj
  }

  readonly serialize = <T extends {}>(state: T) => {
    const sliceState = { ...state[this.reducerKey] } as Record<string, any>
    for (const serializer of this.serializers) {
      sliceState[serializer.key] = serializer.serialize(sliceState[serializer.key])
    }
    return { ...state, [this.reducerKey]: sliceState } as Serialized<T>
  }

  readonly deserialize = (state: any) => {
    const sliceState = { ...state[this.reducerKey] } as Record<string, any>
    for (const serializer of this.serializers) {
      sliceState[serializer.key] = serializer.deserialize(sliceState[serializer.key])
    }
    return { ...state, [this.reducerKey]: sliceState }
  }
}

/** Be cautious because serialized state is not the same as the original state */
export type Serialized<T> = T & { [SerializedTag]: true }

// eslint-disable-next-line no-useless-assignment
const SerializedTag = Symbol('serialized')
